import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { updateConfigSettings } from 'api/Admin/DevSettings';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import Allowed from 'components/shared/Permissions/Allowed';
import Select from 'components/shared/Select/Select';
import Switch from 'components/shared/Switch/Switch';

const FeatureFlagModal = ({ isOpen, handleClose: onClose, item: initialData }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isEdit = initialData ? true : false;

  const formik = useFormik({
    initialValues: {
      name: initialData?.key || '',
      type: initialData?.type || '',
      value:
        initialData?.type === 'boolean' ? (initialData?.value ?? false) : (initialData?.value ?? '')
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Feature flag is required.'),
      type: Yup.string().required('Type is required.'),
      value: Yup.string().when('type', {
        is: 'string',
        then: (schema) => schema.required('Default value is required.')
      })
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const key = `featureFlags.${values.name}`;
      const payload = {
        configArray: [
          {
            key,
            originalKey: initialData?.key ? `featureFlags.${initialData.key}` : undefined,
            value: values.value,
            kind: values.type
          }
        ]
      };
      try {
        await updateConfigSettings(navigate, payload);
        queryClient.invalidateQueries(['featureFlags']);
        showAlert({
          title: isEdit ? 'Feature flag updated.' : 'Feature flag created.',
          color: 'success'
        });
        handleClose();
      } catch (err) {
        showAlert({
          title: 'Error creating feature flag.',
          color: 'danger'
        });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      title={isEdit ? 'Edit Feature Flag' : 'New Feature Flag'}
      slideFromRight
      footer={
        <div className="flex w-full justify-between">
          <Button text="Cancel" outlined onClick={handleClose} />
          <Allowed requiredPermissions="admin_feature_flag.create">
            <Button
              text={isEdit ? 'Save' : 'Create'}
              onClick={formik.submitForm}
              loading={formik.isSubmitting}
            />
          </Allowed>
        </div>
      }>
      <div className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium text-neutral-700">Feature Flag</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-[10px] select-none text-sm text-neutral-500">
              featureFlags.
            </span>
            <Input
              placeholder="featureGroup.featureName"
              value={formik.values.name}
              onChange={(e) => {
                formik.setFieldValue('name', e.target.value);
                formik.validateField('name');
              }}
              error={formik.errors.name}
              className="pl-[105px]"
            />
          </div>
        </div>

        <div>
          <Select
            label="Type"
            options={[
              { label: 'Boolean', value: 'boolean' },
              { label: 'String', value: 'string' }
            ]}
            value={
              formik.values.type
                ? {
                    label: formik.values.type[0].toUpperCase() + formik.values.type.slice(1),
                    value: formik.values.type
                  }
                : null
            }
            onChange={(option) => {
              const type = option?.value || '';

              formik.setFieldValue('type', type);

              if (type === 'boolean') {
                formik.setFieldValue(
                  'value',
                  initialData?.type === 'boolean' ? initialData?.value : false
                );
              } else if (type === 'string') {
                formik.setFieldValue(
                  'value',
                  initialData?.type === 'string' ? initialData?.value : ''
                );
              }
              Promise.resolve().then(() => {
                formik.validateField('type');
              });
            }}
            placeholder="Select Type"
            error={formik.errors.type}
          />
        </div>
        <div>
          {formik.values.type && (
            <div>
              <label className="text-sm font-medium text-neutral-700">Setting</label>
              <div className="mt-1">
                {formik.values.type === 'boolean' ? (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formik.values.value}
                      onChange={(value) => formik.setFieldValue('value', value)}
                    />
                    <span className="text-sm font-medium text-neutral-700">
                      {formik.values.value ? 'True' : 'False'}
                    </span>
                  </div>
                ) : (
                  <Input
                    placeholder="Enter default string value"
                    value={formik.values.value}
                    onChange={(e) => formik.setFieldValue('value', e.target.value)}
                    error={formik.errors.value}
                    data-qa="feature-flag-string-value"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default withErrorBoundary(FeatureFlagModal);
